import * as React from 'react';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, BookOpen, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { ImageUpload } from './ImageUpload';
import {
  fetchMergedCourses,
  updateCourseWithSync,
  insertCourseWithSync,
  deleteCourseWithSync,
} from '../../services/courseSyncService';

interface Course {
  id: string;
  course_name: string;
  institution: string;
  category: string;
  start_date: string;
  end_date: string | null;
  status: 'ongoing' | 'completed' | 'paused' | 'cancelled';
  description: string | null;
  image_url: string | null;
  is_current: boolean;
  created_at: string;
  updated_at: string;
  profile_id: string;
}

interface CourseFormData {
  course_name: string;
  institution: string;
  category: string;
  start_date: string;
  end_date: string;
  status: 'ongoing' | 'completed' | 'paused' | 'cancelled';
  description: string;
  image_url: string;
  is_current: boolean;
}

const initialFormData: CourseFormData = {
  course_name: '',
  institution: '',
  category: '',
  start_date: '',
  end_date: '',
  status: 'ongoing',
  description: '',
  image_url: '',
  is_current: false
};

const categoryOptions = [
  'Akademik',
  'Bahasa',
  'Teknologi',
  'Seni & Desain',
  'Olahraga',
  'Musik',
  'Bisnis',
  'Lainnya'
];

const statusOptions = [
  { value: 'ongoing', label: 'Sedang Berlangsung' },
  { value: 'completed', label: 'Selesai' },
  { value: 'paused', label: 'Ditunda' },
  { value: 'cancelled', label: 'Dibatalkan' }
];

export const CourseManagement: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CourseFormData>(initialFormData);

  useEffect(() => {
    fetchCourses();
  }, []);

  const getProfileId = async (): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profileByUser } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (profileByUser?.id) return profileByUser.id;
      }
      
      const { data: defaultProfile } = await supabase
        .from('profiles')
        .select('id')
        .limit(1)
        .maybeSingle();
      return defaultProfile?.id || null;
    } catch (e) {
      console.warn('Could not get profile ID:', e);
      return null;
    }
  };

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchMergedCourses();
      setCourses(data || []);
    } catch (err: any) {
      console.error('Error fetching courses:', err);
      setError(err?.message || 'Gagal memuat data kursus');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CourseFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const profileId = await getProfileId();

      const courseData: Record<string, any> = {
        course_name: formData.course_name,
        institution: formData.institution,
        category: formData.category,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        status: formData.status,
        description: formData.description || null,
        image_url: formData.image_url || null,
        is_current: formData.is_current || false
      };

      if (profileId) {
        courseData.profile_id = profileId;
      }

      if (editingId) {
        await updateCourseWithSync(editingId, courseData);
        setSuccess('Data kursus berhasil diperbarui!');
      } else {
        await insertCourseWithSync(courseData);
        setSuccess('Data kursus berhasil ditambahkan!');
      }

      setFormData(initialFormData);
      setShowForm(false);
      setEditingId(null);
      await fetchCourses();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error saving course:', err);
      setError(err?.message || 'Gagal menyimpan data kursus');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (course: Course) => {
    setFormData({
      course_name: course.course_name,
      institution: course.institution,
      category: course.category,
      start_date: course.start_date ? course.start_date.substring(0, 10) : '',
      end_date: course.end_date ? course.end_date.substring(0, 10) : '',
      status: course.status,
      description: course.description || '',
      image_url: course.image_url || '',
      is_current: course.is_current || false
    });
    setEditingId(course.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Apakah Anda yakin ingin menghapus data kursus ini?')) {
      return;
    }

    try {
      setError(null);
      await deleteCourseWithSync(id);

      setSuccess('Data kursus berhasil dihapus!');
      await fetchCourses();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting course:', err);
      setError(err?.message || 'Gagal menghapus data kursus');
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setShowForm(false);
    setEditingId(null);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      ongoing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Sedang Berlangsung' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Selesai' },
      paused: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Ditunda' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Dibatalkan' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.ongoing;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Memuat data kursus...</span>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kelola Kursus</h1>
            <p className="mt-1 text-sm text-gray-500">
              Tambah, edit, atau hapus riwayat kursus yang akan ditampilkan di portofolio
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Tambah Kursus
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="text-sm text-green-700">{success}</div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="mb-6 bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {editingId ? 'Edit Data Kursus' : 'Tambah Data Kursus Baru'}
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="course_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nama Kursus *
                </label>
                <input
                  type="text"
                  id="course_name"
                  required
                  value={formData.course_name}
                  onChange={(e) => handleInputChange('course_name', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nama kursus"
                />
              </div>

              <div>
                <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-2">
                  Institusi *
                </label>
                <input
                  type="text"
                  id="institution"
                  required
                  value={formData.institution}
                  onChange={(e) => handleInputChange('institution', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nama institusi/lembaga"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Kategori *
                </label>
                <select
                  id="category"
                  required
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Pilih kategori</option>
                  {categoryOptions.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  id="status"
                  required
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value as CourseFormData['status'])}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Tanggal Mulai *
                </label>
                <input
                  type="date"
                  id="start_date"
                  required
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Tanggal Selesai
                </label>
                <input
                  type="date"
                  id="end_date"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  disabled={formData.is_current}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_current"
                checked={formData.is_current || false}
                onChange={(e) => {
                  const isCurrent = e.target.checked;
                  handleInputChange('is_current', isCurrent);
                  if (isCurrent) {
                    handleInputChange('end_date', '');
                    handleInputChange('status', 'ongoing');
                  }
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_current" className="ml-2 block text-sm text-gray-700">
                Sedang mengikuti kursus ini
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gambar Kursus
              </label>
              <ImageUpload
                value={formData.image_url}
                onChange={(url) => handleInputChange('image_url', url)}
                bucket="portfolio-images"
                folder="courses"
                accept="image/*"
                maxSize={5}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Deskripsi
              </label>
              <textarea
                id="description"
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Deskripsi kursus, materi yang dipelajari, atau catatan khusus..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4 mr-2" />
                Batal
              </button>
              <button
                type="submit"
                disabled={saving || !formData.course_name || !formData.institution || !formData.category || !formData.start_date}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {editingId ? 'Perbarui' : 'Simpan'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Course List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Riwayat Kursus ({courses.length})
          </h3>
        </div>

        {courses.length === 0 ? (
          <div className="p-6 text-center">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Belum ada data kursus</h3>
            <p className="mt-1 text-sm text-gray-500">
              Mulai dengan menambahkan riwayat kursus pertama.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Tambah Kursus
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kursus
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institusi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kategori
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Periode
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {courses.map((course) => (
                    <tr key={course.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {course.image_url && (
                            <img
                              src={course.image_url}
                              alt={course.course_name}
                              className="h-10 w-10 rounded-lg object-cover mr-3"
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {course.course_name}
                            </div>
                            {course.description && (
                              <div className="text-sm text-gray-500">
                                {course.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {course.institution}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {course.category}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <div>
                            {new Date(course.start_date).toLocaleDateString('id-ID')}
                            {course.end_date ? (
                              <> - {new Date(course.end_date).toLocaleDateString('id-ID')}</>
                            ) : (
                              course.is_current && <> - Sekarang</>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(course.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(course)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(course.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};